import { defineStore } from "pinia";
import { pickAutoplayWinner } from "%/lib/autoplay-arbitration";
import AutoplayError from "%/errors/autoplay-error";

const instances = new Map();

export const useAutoplayCoordinatorStore = defineStore("autoplay-coordinator", {
  state: () => ({
    activeId: null,
    pinnedId: null,
    muteByGroup: {},
  }),
  getters: {
    isMutedForGroup(state) {
      return groupKey => {
        return groupKey in state.muteByGroup ? state.muteByGroup[groupKey] : true;
      };
    },
  },
  actions: {
    register(id, { autoplay, muteGroupKey, defaultMuted = true, setActive, setMuted }) {
      instances.set(id, { ratio: 0, top: 0, autoplay, muteGroupKey, setActive, setMuted });
      if (!(muteGroupKey in this.muteByGroup)) {
        this.muteByGroup = { ...this.muteByGroup, [muteGroupKey]: defaultMuted };
      }
      this.reconcile();
    },
    unregister(id) {
      instances.delete(id);
      if (this.activeId === id) {
        this.activeId = null;
      }
      if (this.pinnedId === id) {
        this.pinnedId = null;
      }
      this.reconcile();
    },
    reportRatio(id, ratio, top = 0) {
      const inst = instances.get(id);
      if (!inst) {
        return;
      }
      inst.ratio = Number(ratio) || 0;
      inst.top = Number(top) || 0;
      this.reconcile();
    },
    claim(id) {
      if (!instances.has(id)) {
        throw new AutoplayError("Cannot claim playback for a video the autoplay coordinator does not know", {
          id,
          registeredIds: [...instances.keys()],
        });
      }
      if (this.activeId === id) {
        return;
      }
      if (this.activeId && instances.has(this.activeId)) {
        instances.get(this.activeId).setActive(false);
      }
      this.activeId = id;
      this.pinnedId = id;
      instances.get(id).setActive(true);
    },
    reconcile() {
      const entries = [...instances.entries()].map(([id, inst]) => ({
        id, ratio: inst.ratio, top: inst.top, autoplay: inst.autoplay,
      }));
      const winnerId = pickAutoplayWinner(entries, this.pinnedId);
      if (this.pinnedId !== null && winnerId !== this.pinnedId) {
        this.pinnedId = null;
      }
      if (winnerId === this.activeId) {
        return;
      }
      if (this.activeId && instances.has(this.activeId)) {
        instances.get(this.activeId).setActive(false);
      }
      this.activeId = winnerId;
      if (winnerId && instances.has(winnerId)) {
        const inst = instances.get(winnerId);
        inst.setMuted(this.isMutedForGroup(inst.muteGroupKey));
        inst.setActive(true);
      }
    },
    setGroupMuted(groupKey, muted) {
      this.muteByGroup = { ...this.muteByGroup, [groupKey]: muted };
      instances.forEach(inst => {
        if (inst.muteGroupKey === groupKey) {
          inst.setMuted(muted);
        }
      });
    },
  },
});

export function __resetAutoplayInstancesForTest() {
  instances.clear();
}
